/**
 * API Types
 *
 * Type definitions for Agent API requests and responses.
 * Covers payment actions, saved cards, and error handling.
 */

import type { PaymentStatus } from './payment-status';

/**
 * Submit Payment Request
 *
 * POST /api/payment/submit
 * Body is empty (session context sent via x-session-id header)
 *
 * @deprecated paymentId field is no longer used; session context is sent via x-session-id header
 */
export interface SubmitPaymentRequest {
  /** @deprecated Session context is sent via x-session-id header */
  paymentId?: string;
}

/**
 * Payment Response
 *
 * Response from submit payment endpoint
 */
export interface PaymentResponse {
  /**
   * Payment identifier
   */
  paymentId: string;

  /**
   * Payment status from Payment Orchestrator
   */
  status: PaymentStatus;

  /**
   * Gateway transaction ID (if successful)
   */
  transactionId?: string;

  /**
   * Error code (if failed)
   */
  errorCode?: string;

  /**
   * Error message (if failed)
   */
  errorMessage?: string;

  /**
   * Success or error message
   */
  message?: string;
}

/**
 * Cancel Payment Response
 */
export interface CancelPaymentResponse {
  /**
   * Payment identifier
   */
  paymentId: string;

  /**
   * Cancellation status
   */
  status: 'CANCELLED';

  /**
   * Cancellation timestamp (ISO 8601)
   */
  timestamp: string;

  /**
   * Confirmation message
   */
  message?: string;
}

/**
 * Field types for reset action
 *
 * VOICE flow only - allows resetting individual fields
 */
export type FieldType =
  | 'CARD_NUMBER'
  | 'CVC'
  | 'EXPIRY_DATE'
  | 'CARDHOLDER_NAME';

/**
 * Reset Response
 *
 * PATCH /api/payment/reset-pan
 * PATCH /api/payment/reset-cvv
 * PATCH /api/payment/restart
 * VOICE flow only
 */
export interface ResetFieldResponse {
  /**
   * Payment identifier
   */
  paymentId: string;

  /**
   * Reset timestamp (ISO 8601)
   */
  resetAt: string;

  /**
   * Confirmation message
   */
  message?: string;
}

/**
 * Saved Card API Response
 *
 * GET /api/payments/{paymentId}/card-tokens
 *
 * Returns array of CardTokenResponse from PCI Tokeniser.
 * Backend filters to only return ACTIVE tokens.
 *
 * Re-exports CardTokenResponse and SavedCard from voice-dtmf.types
 */
export type { SavedCard, CardTokenResponse } from './voice-dtmf.types';

/**
 * Delete Saved Card Response
 *
 * DELETE /api/payments/{paymentId}/card-tokens/{cardTokenId}
 *
 * Returns 204 No Content on success (no response body)
 */
export interface DeleteSavedCardResponse {
  /**
   * Deletion status (only present if backend sends body)
   */
  status?: 'deleted';

  /**
   * Deleted card token ID (only present if backend sends body)
   */
  cardTokenId?: string;

  /**
   * Confirmation message (only present if backend sends body)
   */
  message?: string;
}

/**
 * API Error Response
 *
 * Structured error response from backend
 */
export interface ApiErrorResponse {
  /**
   * HTTP status code
   */
  statusCode: number;

  /**
   * Machine-readable error code
   * E.g., "SESSION_EXPIRED", "PAYMENT_DECLINED"
   */
  errorCode: string;

  /**
   * Human-readable error message
   */
  message: string;

  /**
   * Additional error details
   */
  details?: unknown;
}

/**
 * API Error Class
 *
 * Thrown when API requests fail
 * Implements ApiErrorResponse interface
 */
export class ApiError extends Error implements ApiErrorResponse {
  statusCode: number;
  errorCode: string;
  details?: unknown;

  constructor(
    statusCode: number,
    errorCode: string,
    message: string,
    details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.errorCode = errorCode;
    this.details = details;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (
      'captureStackTrace' in Error &&
      typeof Error.captureStackTrace === 'function'
    ) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

/**
 * Retry-able HTTP status codes
 *
 * These status codes should trigger automatic retry with exponential backoff
 */
export const RETRYABLE_STATUS_CODES = [
  408, // Request Timeout
  429, // Too Many Requests
  500, // Internal Server Error
  502, // Bad Gateway
  503, // Service Unavailable
  504, // Gateway Timeout
] as const;

/**
 * Payment Declined Response
 *
 * HTTP 402 from POST /api/payment/submit — payment was declined by the gateway.
 * Present in ApiError.details when statusCode === 402.
 */
export interface PaymentDeclinedResponse {
  paymentId: string;
  sessionId: string;
  declinedAttempts: number;
  canRetry: boolean;
}

/**
 * Non-retry-able 4xx status codes
 *
 * These indicate client errors and should NOT be retried
 * Exception: 401 (re-authentication attempted once)
 */
export const NON_RETRYABLE_STATUS_CODES = [
  400, // Bad Request
  402, // Payment Required / Payment Declined
  403, // Forbidden
  404, // Not Found
  422, // Unprocessable Entity
] as const;

/**
 * Error severity levels for user-facing error messages
 */
export type ErrorSeverity = 'info' | 'warning' | 'error' | 'fatal';

/**
 * User-friendly error messages by status code
 */
export const ERROR_MESSAGES: Record<
  number,
  { severity: ErrorSeverity; message: string }
> = {
  401: {
    severity: 'warning',
    message: 'Session expired. Please refresh the page.',
  },
  403: {
    severity: 'error',
    message:
      'Access denied. You do not have permission to perform this action.',
  },
  404: {
    severity: 'error',
    message: 'Session not found. Please refresh the page.',
  },
  410: {
    severity: 'warning',
    message: 'Session expired, please refresh',
  },
  500: {
    severity: 'error',
    message: 'Server error, please try again',
  },
  0: {
    // Network error (no response)
    severity: 'warning',
    message: 'Connection lost, retrying...',
  },
} as const;
